import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ImageInput from '@/components/ui/custom/dataInput/ImageInput';
import NumberInput from '@/components/ui/custom/dataInput/NumberInput';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import Hackathon from '@/models/Hackathon';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type HackathonFormFields = Pick<Hackathon, 'edition' | 'title' | 'content'>;

export type HackathonFields = HackathonFormFields & {
  banner?: File | null;
};

type SubmitFn = (hackathon: HackathonFields) => void;

type HackathonFormProps = {
  hackathon?: Hackathon;
  onSubmit: SubmitFn;
  onCancel?: () => void;
  disable?: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
  setHasChanges?: (hasChanges: boolean) => void;
};

export default function HackathonForm({
  hackathon,
  hasChanges,
  setHasChanges,
  onSubmit,
  onCancel,
  disable,
  isLoading,
}: HackathonFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<HackathonFormFields>({
    defaultValues: hackathon,
  });

  const [t] = useTranslation('translation', {
    keyPrefix: 'hackathon.form.hackathon',
  });

  const [bannerFile, setBannerFile] = useState<File | null>();

  const onSubmitClean = (fields: HackathonFormFields) =>
    onSubmit({ ...fields, banner: bannerFile });

  const onSelectBanner = (file: File | null) => {
    setBannerFile(file);
    setHasChanges?.(true);
  };

  useEffect(() => {
    const subscription = watch(fields => {
      setHasChanges?.(!isEqual(fields, hackathon));
    });
    return () => subscription.unsubscribe();
  }, [watch, setHasChanges, hackathon]);

  return (
    <form
      className="flex flex-col gap-4 overflow-hidden w-full max-w-[400px]"
      onSubmit={handleSubmit(onSubmitClean)}
    >
      <NumberInput
        label={t('edition.label')}
        placeholder={t('edition.placeholder')}
        labelPosition="left"
        disabled={disable}
        className={{
          label: 'w-24 font-500 text-14',
          input: 'font-500 text-14 text-left px-3 placeholder:font-500',
          div: 'grow',
        }}
        errorLabelText={errors.edition?.message}
        {...register('edition', {
          required: t('required'),
          validate: value =>
            Number.isInteger(value) || t('edition.numberValidate'),
          valueAsNumber: true,
        })}
        onBlur={undefined}
      />
      <TextInput
        label={t('theme.label')}
        placeholder={t('theme.placeholder')}
        errorLabelText={errors.title?.message}
        labelPosition="left"
        disabled={disable}
        className={{ label: 'w-24 font-500', input: 'font-500' }}
        {...register('title', {
          required: t('required'),
        })}
      />

      <TextInput
        label={t('content.label')}
        placeholder={t('content.placeholder')}
        labelPosition="left"
        disabled={disable}
        className={{ label: 'w-24 font-500', input: 'font-500' }}
        errorLabelText={errors.content?.message}
        {...register('content')}
      />
      <div className="flex flex-col gap-1.5">
        <Text text={t('banner.label')} className="font-500" />
        <ImageInput
          testId="hackathonBanner"
          isDisabled={disable}
          image={
            bannerFile !== undefined
              ? bannerFile
              : hackathon?.banner?.url ?? null
          }
          onChange={event => onSelectBanner(event.target.files?.[0] ?? null)}
          onRemove={() => onSelectBanner(null)}
        />
      </div>
      <ConditionalRenderer condition={!disable}>
        <SaveCancelGroup
          loading={isLoading}
          save={{
            type: 'submit',
            disabled: !hasChanges || !isEmpty(errors),
          }}
          cancel={{
            onClick: () => {
              reset(hackathon);
              setBannerFile(undefined);
              setHasChanges?.(false);
              onCancel?.();
            },
          }}
        />
      </ConditionalRenderer>
    </form>
  );
}
